import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Input } from '../../../components/ui/input';

// Zod schema for 6-digit authentication code validation
const codeInputSchema = z.object({
  code: z
    .string()
    .min(1, 'Please enter the authentication code')
    .length(6, 'Authentication code must be exactly 6 digits')
    .regex(/^\d{6}$/, 'Authentication code must contain only numbers')
});

type CodeInputFormValues = z.infer<typeof codeInputSchema>;

export const CodeInput = ({
  onComplete,
  onBack,
  isLoading,
  error,
  isMfaSetup
}: {
  onComplete?: (code: string) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
  error?: string | Array<string> | null;
  isMfaSetup?: boolean;
  userEmail?: string;
}): JSX.Element => {
  const form = useForm<CodeInputFormValues>({
    defaultValues: {
      code: ''
    },
    resolver: zodResolver(codeInputSchema),
    mode: 'onTouched'
  });

  const handleSubmit = async (values: CodeInputFormValues) => {
    if (values.code.length === 6) {
      await onComplete?.(values.code);
    }
  };

  return (
    <div className="flex flex-col gap-3.5">
      {isMfaSetup && (
        <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ink)]">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-blue">
            3
          </span>
          Step 3: Enter 6-digit code
        </h2>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(handleSubmit)(e);
        }}
        className="flex flex-col gap-3.5"
      >
        <div>
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            spellCheck={false}
            placeholder="000000"
            pattern="[0-9]*"
            maxLength={6}
            className="h-12 text-center text-2xl tracking-widest"
            aria-invalid={!!form.formState.errors.code}
            aria-describedby={form.formState.errors.code ? 'otp-error' : 'otp-hint'}
            {...form.register('code', {
              onChange: (e) => {
                e.target.value = e.target.value.replace(/\D/g, '');
                form.setValue('code', e.target.value);
              }
            })}
          />
          {form.formState.errors.code && (
            <p id="otp-error" aria-live="polite" className="mt-1.5 text-xs text-loss">
              {form.formState.errors.code.message}
            </p>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-loss/20 bg-loss/5 px-4 py-3 text-sm text-loss">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              {Array.isArray(error) ? (
                <div className="space-y-1">
                  {error.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </div>
              ) : (
                error
              )}
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Verifying...</span>
            </div>
          ) : isMfaSetup ? (
            'Verify & Complete Setup'
          ) : (
            'Verify & Continue'
          )}
        </button>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={onBack}
            className="cursor-pointer text-sm font-medium text-brand-blue hover:opacity-80"
          >
            Back to login
          </button>
        </div>
      </form>
    </div>
  );
};
